import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { SearchBar, TableFlat, FormModal } from "@veorest/ui";
import { Handshake, Shield, Plus, Copy, Check, Eye } from "lucide-react";
import {
  fetchAdminPartners,
  mintAdminPartnerPasscode,
  type AdminPartner,
} from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, statusPillClass } from "../lib/format";
import { partnerDetailPath } from "../routes";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "onboarding", label: "Onboarding" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
] as const;

function slugifyName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

type PartnerPasscode = {
  passcode: string;
  name: string;
  expiresAt: string;
};

/** Iraqi mobile → 07XXXXXXXXX (11 digits). Accepts +964 / 00964 / 7-forms. */
function normalizeIraqiPhone(raw: string): string {
  let digits = raw.replace(/[^0-9]/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("964")) digits = `0${digits.slice(3)}`;
  if (digits.length === 10 && digits.startsWith("7")) digits = `0${digits}`;
  return digits;
}

function isValidIraqiPhone(raw: string): boolean {
  return /^07[0-9]{9}$/.test(normalizeIraqiPhone(raw));
}

export default function PartnersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const [createForm, setCreateForm] = useState({
    name: "",
    phone: "",
    slug: "",
    email: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [issuedPasscode, setIssuedPasscode] = useState<PartnerPasscode | null>(null);
  const [copiedField, setCopiedField] = useState<"passcode" | null>(null);

  const query = useQuery({
    queryKey: ["admin-partners", search, page],
    queryFn: () => fetchAdminPartners({ search, page, limit: 25 }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      mintAdminPartnerPasscode({
        name: createForm.name.trim(),
        phone: normalizeIraqiPhone(createForm.phone),
        email: createForm.email.trim() || undefined,
        slug: createForm.slug.trim() || undefined,
      }),
    onSuccess: (data) => {
      setIssuedPasscode({
        passcode: data.passcode,
        name: data.partner?.name ?? createForm.name.trim(),
        expiresAt: data.expires_at,
      });
      setCreateOpen(false);
      setCreateForm({ name: "", phone: "", slug: "", email: "" });
      setSlugTouched(false);
      qc.invalidateQueries({ queryKey: ["admin-partners"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function copyCredential(field: "passcode", value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  }

  const items = (query.data?.items ?? []).filter(
    (p) => !status || p.status === status
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Partner",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return (
          <div className="admin-row-main">
            <span className="admin-row-avatar" aria-hidden>
              <Handshake className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="min-w-0">
              <p className="admin-row-title">{p.name}</p>
              <p className="admin-row-sub">{p.slug}</p>
            </div>
          </div>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return <span className={statusPillClass(p.status)}>{p.status}</span>;
        },
      },
      {
        key: "linked",
        header: "Linked tenants",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return <span className="tabular font-bold">{p.linked_tenants_count}</span>;
        },
      },
      {
        key: "leave",
        header: "Leave requests",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return p.leave_requests_count > 0 ? (
            <span className="text-amber-600 font-bold">{p.leave_requests_count}</span>
          ) : (
            <span className="text-[var(--text-muted)]">0</span>
          );
        },
      },
      {
        key: "created",
        header: "Created",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return <span className="text-[var(--text-muted)]">{formatDate(p.created_at)}</span>;
        },
      },
      {
        key: "actions",
        header: "",
        render: (row: Record<string, unknown>) => {
          const p = row as AdminPartner;
          return (
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(partnerDetailPath(p.id));
            }}
          >
            <Eye className="h-3.5 w-3.5 inline" />
            View
          </button>
          );
        },
      },
    ],
    [navigate]
  );

  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / 25));

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Logistics"
        title="Partners"
        subtitle="Logistics operators. They can create clients, manage partner drivers, and optionally fall back to OELOX global drivers."
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            Create partner
          </button>
        }
      />

      <AdminPanel
        toolbar={
          <>
            <SearchBar
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search partners…"
              className="admin-search"
            />
            <div className="customer-filter-chips admin-filter-chips">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  className={`customer-filter-chip ${status === f.key ? "customer-filter-chip--active" : ""}`}
                  onClick={() => {
                    setStatus(f.key);
                    setPage(1);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </>
        }
      >
        {query.isLoading ? (
          <p className="admin-muted p-6">Loading partners…</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Handshake className="h-5 w-5" />}
            title="No partners"
            body="Create a logistics partner to onboard clients and partner drivers."
            actionLabel="Create partner"
            onAction={() => setCreateOpen(true)}
            actionIcon={<Plus className="h-4 w-4" />}
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={columns}
              data={items as Array<Record<string, unknown> & { id: string }>}
              keyField="id"
              onRowClick={(row) => navigate(partnerDetailPath(row.id))}
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={query.data?.total ?? 0}
        label="partners"
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />

      <FormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create partner"
        subtitle="Generate a single-use passcode the partner redeems in the partner app or web."
        eyebrow="Partners"
        icon={<Plus className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={
                createMutation.isPending ||
                !createForm.name.trim() ||
                !isValidIraqiPhone(createForm.phone)
              }
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? "Generating…" : "Generate passcode"}
            </button>
          </div>
        }
      >
        <div className="space-y-3 p-1">
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Partner name
            <input
              className="input-base mt-1"
              placeholder="Acme Logistics"
              value={createForm.name}
              onChange={(e) => {
                const name = e.target.value;
                setCreateForm((f) => ({
                  ...f,
                  name,
                  slug: slugTouched ? f.slug : slugifyName(name),
                }));
              }}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Phone number
            <input
              className="input-base mt-1"
              type="tel"
              inputMode="tel"
              required
              placeholder="07XXXXXXXXX"
              maxLength={11}
              value={createForm.phone}
              onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          {createForm.phone.trim() && !isValidIraqiPhone(createForm.phone) ? (
            <p className="text-xs font-semibold text-amber-600">
              Enter an Iraqi mobile number: 07XXXXXXXXX (11 digits).
            </p>
          ) : null}
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Slug (optional)
            <input
              className="input-base mt-1"
              placeholder="auto-generated from name"
              value={createForm.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setCreateForm((f) => ({ ...f, slug: e.target.value }));
              }}
            />
          </label>
          <label className="block text-sm font-medium text-[var(--text-muted)]">
            Email (optional)
            <input
              className="input-base mt-1"
              type="email"
              placeholder="ops@partner.com"
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <p className="text-xs text-[var(--text-muted)]">
            The partner redeems the passcode in the partner app or web to create their own login.
            The code is single-use and expires 6 hours after it is generated if not used.
          </p>
        </div>
      </FormModal>

      <FormModal
        open={!!issuedPasscode}
        onClose={() => setIssuedPasscode(null)}
        title="Partner passcode"
        subtitle="Single-use code — valid for 6 hours if unused."
        eyebrow="Partners"
        icon={<Shield className="w-5 h-5" />}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              className="btn-primary"
              onClick={() => setIssuedPasscode(null)}
            >
              Done
            </button>
          </div>
        }
      >
        {issuedPasscode ? (
          <div className="space-y-4 p-1">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                Partner
              </p>
              <p className="mt-1 text-sm font-bold">{issuedPasscode.name}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                Onboarding passcode
              </p>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-[var(--surface-muted)] px-3 py-2 text-2xl font-black tracking-widest">
                  {issuedPasscode.passcode}
                </code>
                <button
                  type="button"
                  className="btn-ghost px-2"
                  onClick={() => copyCredential("passcode", issuedPasscode.passcode)}
                  aria-label="Copy passcode"
                >
                  {copiedField === "passcode" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              Share it with the partner owner. They open the partner app (or partner web), choose
              “Have a passcode?”, and complete onboarding. Single use — expires 6 hours after
              generation if unused.
            </p>
          </div>
        ) : null}
      </FormModal>



    </div>
  );
}
